import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MindARThree } from 'mind-ar/dist/mindar-face-three.prod.js';
import { makeEarlobeCompute, EARLOBE_DEFAULTS } from '../../utils/earlobeAnchor';
import config from '../../config/config';
import './Earrings.css';
import { FilterAndCaptureButtons } from '../../utils/screenCapture';

const getManifestNumber = (manifest, field, fallback) => {
  const val = Number(manifest?.[field]);
  return Number.isFinite(val) ? val : fallback;
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Fallback when manifest provides no physical dimensions or category:
// 0.30 of ear-to-ear width ≈ 45mm on a 150mm face, a typical medium drop/jhumka.
const EARRING_SCALE_FACTOR = 0.30;

// Same calibration as 2D; face-relative scaling is applied in the animation loop.
const FACE_REFERENCE_MM = 150;
const SIZE_PRESETS = {
  stud:       { heightMm: 10 },
  hoopSmall:  { heightMm: 25 },
  dropSmall:  { heightMm: 32 },
  dropMedium: { heightMm: 48 },
  chandelier: { heightMm: 65 },
};
const CATEGORY_LIMITS = {
  stud:       { min: 0.05, max: 0.18 },
  hoopSmall:  { min: 0.12, max: 0.30 },
  dropSmall:  { min: 0.15, max: 0.40 },
  dropMedium: { min: 0.25, max: 0.60 },
  chandelier: { min: 0.35, max: 0.85 },
};
const DEFAULT_LIMITS = { min: 0.05, max: 0.85 };

// defaultScale is a vendor correction multiplier on top of the physical size (1.0 = no correction).
// Priority: physicalHeightMm → category preset → global default; defaultScale applied to all paths.
const resolveScaleFactor = (manifest) => {
  const ds = Number(manifest?.defaultScale);
  const multiplier = Number.isFinite(ds) && ds > 0 ? ds : 1.0;
  const heightMm = Number(manifest?.physicalHeightMm);
  if (Number.isFinite(heightMm) && heightMm > 0) return (heightMm / FACE_REFERENCE_MM) * multiplier;
  const preset = SIZE_PRESETS[manifest?.category];
  if (preset) return (preset.heightMm / FACE_REFERENCE_MM) * multiplier;
  return EARRING_SCALE_FACTOR * multiplier;
};

const resolveLimits = (manifest) => CATEGORY_LIMITS[manifest?.category] ?? DEFAULT_LIMITS;

function Earrings() {
  const { sku } = useParams();
  const category = 'earrings';
  const containerRef = useRef(null);
  const mindarThreeRef = useRef(null);
  const sceneRef = useRef(null);
  const anchorRef = useRef({
    leftEarAnchor: null,
    rightEarAnchor: null,
    occluderAnchor: null
  });
  const modelsRef = useRef({
    occluder: null,
    leftEarring: null,
    rightEarring: null
  });
  const rendererRef = useRef(null);
  const [selectedFilter, setSelectedFilter] = useState('none');

  // Cartoon shader
  const cartoonMaterial = useMemo(() => new THREE.ShaderMaterial({
    uniforms: {
      lightDirection: { value: new THREE.Vector3(0, 1, 1).normalize() },
      toonLevel: { value: 4.0 },
      edge: { value: 0.4 }
    },
    vertexShader: `
      varying vec3 vNormal;
      varying vec3 vPosition;
      
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 lightDirection;
      uniform float toonLevel;
      uniform float edge;
      
      varying vec3 vNormal;
      varying vec3 vPosition;
      
      void main() {
        float intensity = dot(vNormal, lightDirection);
        intensity = ceil(intensity * toonLevel) / toonLevel;
        
        // Edge detection
        float edgeIntensity = length(fwidth(vNormal));
        float isEdge = edgeIntensity > edge ? 0.0 : 1.0;
        
        vec3 toonColor = vec3(intensity * isEdge);
        gl_FragColor = vec4(toonColor, 0.5);
      }
    `,
    transparent: true,
    side: THREE.DoubleSide
  }), []);

  // Load and apply face texture
  const texture = useMemo(() => new THREE.TextureLoader().load('/assets/thumbnails/face-mask.png'), []);

  useEffect(() => {
    // console.log('Selected Filter:', selectedFilter);
    if (sceneRef.current && mindarThreeRef.current) {
      const mindarThree = mindarThreeRef.current;
      const { renderer, scene, camera } = sceneRef.current;
      
      // Remove existing face mesh if any
      const existingFaceMesh = scene.children.find(child => child.isFaceMesh);
      if (existingFaceMesh) {
        scene.remove(existingFaceMesh);
      }

      // Add new face mesh based on selected filter
      if (selectedFilter === 'none') {
        renderer.render(scene, camera);
      } else if (selectedFilter === 'cartoon') {
        const faceMesh = mindarThree.addFaceMesh();
        faceMesh.material = cartoonMaterial;
        faceMesh.isFaceMesh = true;
        scene.add(faceMesh);
        renderer.render(scene, camera);
      } else if (selectedFilter === 'animal') {
        const faceMesh = mindarThree.addFaceMesh();
        faceMesh.material.map = texture;
        faceMesh.material.transparent = true;
        faceMesh.material.needsUpdate = true;
        faceMesh.isFaceMesh = true;
        scene.add(faceMesh);
        renderer.render(scene, camera);
      }
    }
  }, [selectedFilter, cartoonMaterial, texture]);

  const fetchManifest = useCallback(async () => {
    try {
      const res = await fetch(`${config.cdnUrl}/models/3D/${category}/${sku}/manifest.json`);
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }, [sku]);

  // Initialize scene and lights
  const initializeScene = (mindarThree) => {
    const { renderer, scene, camera } = mindarThree;
    
    // Add lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.75);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(0, 0, 1);
    camera.add(directionalLight);
    scene.add(camera);

    const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0xbbbbff, 1);
    scene.add(hemisphereLight);

    return { renderer, scene, camera };
  };

  // Create occluder material
  const createOccluderMaterial = () => {
    return new THREE.MeshBasicMaterial({
      colorWrite: false,
      side: THREE.DoubleSide,
      transparent: false,
      opacity: 0,
      depthTest: true,
      depthWrite: true
    });
  };

  // Load occluder model
  const loadOccluderModel = async (occluderMaterial, occluderAnchor) => {
    const occluderLoader = new GLTFLoader();
    try {
      const occluderGltf = await occluderLoader.loadAsync('/assets/models/occluder/headOccluder.glb');
      const occluder = occluderGltf.scene;
      
      occluder.traverse((node) => {
        if (node.isMesh) {
          node.material = occluderMaterial;
          node.renderOrder = -1;
          
          if (!node.morphTargetDictionary || !node.morphTargetInfluences) {
            node.morphTargetDictionary = {};
            node.morphTargetInfluences = [];
          }
        }
      });

      const occluderScale = 0.08;
      occluder.scale.set(occluderScale, occluderScale, occluderScale);
      occluder.position.set(0, -0.15, 0.15);
      occluder.rotation.set(0, 0, 0);
      
      return occluder;
    } catch (error) {
      console.error('Error loading occluder model:', error);
      return null;
    }
  };

  // Load earrings model
  const loadEarringsModel = useCallback(async () => {
    const loader = new GLTFLoader();
    try {
      // Try loading from CDN first if SKU is provided
      if (sku) {
        try {
          const cdnPath = `${config.cdnUrl}/models/3D/${category}/${sku}/scene.glb`;
          const gltf = await loader.loadAsync(cdnPath);
          const model = gltf.scene;
          
          // Store initial scale and position for reference
          model.userData.baseScale = { 
            x: model.scale.x, 
            y: model.scale.y, 
            z: model.scale.z 
          };
          model.userData.basePosition = { 
            x: model.position.x, 
            y: model.position.y, 
            z: model.position.z 
          };
          
          return model;
        } catch (cdnError) {
          console.warn('Failed to load from CDN, falling back to default model:', cdnError);
        }
      }
      
      // Load default model
      const gltf = await loader.loadAsync(config.defaultEarringsModelPath);
      const model = gltf.scene;
      
      // Store initial scale and position for reference
      model.userData.baseScale = { 
        x: model.scale.x, 
        y: model.scale.y, 
        z: model.scale.z 
      };
      model.userData.basePosition = { 
        x: model.position.x, 
        y: model.position.y, 
        z: model.position.z 
      };
      
      return model;
    } catch (error) {
      console.error('Error loading earrings model:', error);
      return null;
    }
  }, [sku, category]);

  // Create earring instance
  const createEarring = (baseModel, rotation, position) => {
    if (!baseModel) return null;
    
    const earring = baseModel.clone();
    earring.traverse((node) => {
      if (node.isMesh) {
        node.material.depthTest = true;
        node.material.depthWrite = true;
        node.renderOrder = 1;
      }
    });
    
    earring.rotation.set(...rotation);
    earring.position.set(...position);
    earring.scale.set(0.09, 0.09, 0.09);
    
    return earring;
  };

  // Update occluder shape based on blendshapes
  const updateOccluderShape = (occluder, blendshapes) => {
    if (!occluder) return;
    
    occluder.traverse((node) => {
      if (node.isMesh) {
        // Map face blendshapes to occluder morphTargets
        const blendshapeMap = [
          'jawOpen',
          'mouthClose',
          'mouthFunnel',
          'mouthPucker',
          'smileLeft',
          'smileRight'
        ];
        
        blendshapeMap.forEach((shape, index) => {
          if (blendshapes[index] !== undefined) {
            node.morphTargetInfluences[index] = blendshapes[index];
          }
        });
      }
    });
  };

  // Initialize all models and scene
  const initializeModels = useCallback(async (mindarThree) => {
    const manifest = await fetchManifest();
    const earlobeOffsets = {
      xOffset: getManifestNumber(manifest, 'earlobeXOffset', EARLOBE_DEFAULTS.xOffset),
      yOffset: getManifestNumber(manifest, 'earlobeYOffset', EARLOBE_DEFAULTS.yOffset),
      zDelta:  getManifestNumber(manifest, 'earlobeZDelta',  EARLOBE_DEFAULTS.zDelta),
    };

    anchorRef.current.leftEarAnchor  = mindarThree.addDynamicAnchor(makeEarlobeCompute('left', earlobeOffsets));
    anchorRef.current.rightEarAnchor = mindarThree.addDynamicAnchor(makeEarlobeCompute('right', earlobeOffsets));
    anchorRef.current.occluderAnchor = mindarThree.addAnchor(168);

    // Load models
    const occluderMaterial = createOccluderMaterial();
    const occluder = await loadOccluderModel(occluderMaterial, anchorRef.current.occluderAnchor);
    const earringBase = await loadEarringsModel();
    
    if (occluder) {
      anchorRef.current.occluderAnchor.group.add(occluder);
      modelsRef.current.occluder = occluder;
      
      // Set up blendshape update listener
      anchorRef.current.occluderAnchor.onUpdate = ({ detail }) => {
        if (detail.rotation && detail.morphTargetInfluences) {
          updateOccluderShape(occluder, detail.morphTargetInfluences);
        }
      };
    }

    if (earringBase) {
      const leftEarring = createEarring(earringBase, [-0.1, 0, 0], [-0.05, -0.5, -0.2]);
      const rightEarring = createEarring(earringBase, [0.1, 0, 0], [0.05, -0.5, -0.2]);

      const scaleFactor = resolveScaleFactor(manifest);
      const limits = resolveLimits(manifest);
      const leftRotDeg  = getManifestNumber(manifest, 'leftRotationOffset', 0);
      const rightRotDeg = getManifestNumber(manifest, 'rightRotationOffset', 0);

      if (leftEarring) {
        leftEarring.rotation.z += (leftRotDeg * Math.PI) / 180;
        leftEarring.userData.scaleFactor = scaleFactor;
        leftEarring.userData.limitsMin   = limits.min;
        leftEarring.userData.limitsMax   = limits.max;
        anchorRef.current.leftEarAnchor.group.add(leftEarring);
        modelsRef.current.leftEarring = leftEarring;
      }

      if (rightEarring) {
        rightEarring.rotation.z += (rightRotDeg * Math.PI) / 180;
        rightEarring.userData.scaleFactor = scaleFactor;
        rightEarring.userData.limitsMin   = limits.min;
        rightEarring.userData.limitsMax   = limits.max;
        anchorRef.current.rightEarAnchor.group.add(rightEarring);
        modelsRef.current.rightEarring = rightEarring;
      }
    }
  }, [loadEarringsModel, fetchManifest]);

  useEffect(() => {
    let mounted = true;
    const currentMindarRef = mindarThreeRef;
    const currentSceneRef = sceneRef;
    const currentModelsRef = modelsRef.current;

    const setup = async () => {
      if (!mounted) return;

      const mindarThree = new MindARThree({
        container: containerRef.current,
        filterMinCF: 0.001,
        filterBeta: 1,
      });

      const { renderer, scene, camera } = initializeScene(mindarThree);
      rendererRef.current = renderer;
      await initializeModels(mindarThree);
      
      if (mounted) {
        currentMindarRef.current = mindarThree;
        currentSceneRef.current = { renderer, scene, camera };

        // Auto-start the AR experience
        await mindarThree.start();

        const leftPos = new THREE.Vector3();
        const rightPos = new THREE.Vector3();
        const anchorCol = new THREE.Vector3();
        let smoothedFaceWidth = 0;

        renderer.setAnimationLoop(() => {
          const { leftEarAnchor, rightEarAnchor } = anchorRef.current;
          const { leftEarring, rightEarring } = modelsRef.current;

          if (leftEarring && rightEarring && leftEarAnchor && rightEarAnchor) {
            leftEarAnchor.group.getWorldPosition(leftPos);
            rightEarAnchor.group.getWorldPosition(rightPos);
            const rawFaceWidth = leftPos.distanceTo(rightPos);

            if (rawFaceWidth > 0) {
              smoothedFaceWidth = smoothedFaceWidth === 0
                ? rawFaceWidth
                : smoothedFaceWidth * 0.85 + rawFaceWidth * 0.15;

              const sf   = leftEarring.userData.scaleFactor ?? EARRING_SCALE_FACTOR;
              const lMin = leftEarring.userData.limitsMin   ?? DEFAULT_LIMITS.min;
              const lMax = leftEarring.userData.limitsMax   ?? DEFAULT_LIMITS.max;
              // MindAR bakes faceScale (metric face width, ~14) into the anchor
              // matrix, so children render faceScale× their local scale. Divide it
              // out so the computed size lands in world units (1 unit ≈ 1 cm).
              const anchorScale = anchorCol.setFromMatrixColumn(leftEarAnchor.group.matrix, 0).length() || 1;
              const targetScale = clamp(smoothedFaceWidth * sf, smoothedFaceWidth * lMin, smoothedFaceWidth * lMax) / anchorScale;

              leftEarring.scale.setScalar(targetScale);
              rightEarring.scale.setScalar(targetScale);

              if (leftEarAnchor.group.visible) {
                const leftZ  = leftEarAnchor.group.matrix.elements[14];
                const rightZ = rightEarAnchor.group.matrix.elements[14];
                const zDiff = leftZ - rightZ;
                leftEarring.visible  = clamp(1 + zDiff / (smoothedFaceWidth * 0.6), 0, 1) > 0.03;
                rightEarring.visible = clamp(1 - zDiff / (smoothedFaceWidth * 0.6), 0, 1) > 0.03;
              }
            }
          }

          renderer.render(scene, camera);
        });
      }
    };

    setup();

    return () => {
      mounted = false;
      if (currentMindarRef.current) {
        currentMindarRef.current.stop();
        currentMindarRef.current.renderer.setAnimationLoop(null);
        currentMindarRef.current.renderer.dispose();
        currentMindarRef.current.renderer.forceContextLoss();

        if (currentSceneRef.current) {
          const { scene, renderer } = currentSceneRef.current;

          Object.values(currentModelsRef).forEach(model => {
            if (model && model.parent) {
              model.parent.remove(model);
            }
          });

          renderer.render(scene, currentSceneRef.current.camera);
          renderer.clear();
        }
      }
      containerRef.current?.replaceChildren();
    };
  }, [initializeModels]);

  // Add function to get current renderer and scene info
  const getRendererInfo = () => {
    return {
      mindarThree: mindarThreeRef.current,
      models: modelsRef.current
    };
  };

  return (
    <div className="container">
      <div className="ar-container" ref={containerRef}>
        <video className="video"></video>
      </div>
      <div className="control-panel">
        <FilterAndCaptureButtons 
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          getRendererInfo={getRendererInfo}
        />
      </div>
    </div>
  );
}

export default Earrings;